import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Spinner from '../../../../bootstrap/Spinner';
import { ticketTracking } from '../../../../redux/action/busAction';
import { PaymentHelper } from '../PaymentHelper';
import {
  HelperText,
  MpesaBrand,
  MpesaButton,
  MpesaButtonGroup,
  MpesaField,
  MpesaInput,
  MpesaLabel,
  MpesaWrapper,
  StatusMessage,
} from './Mpesa.styles';

const MPESA_WAIT_TIME = 30000;

const Mpesa = ({ passengerInformation, allBookingInformation, setLoading, validateForm }) => {
  const { webSettingData } = useSelector((state) => state.busLists);
  const fareSummery = useSelector((state) => state?.busLists?.fareSummery);
  const dispatch = useDispatch();
  const history = useHistory();
  const timerRef = useRef(null);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | requesting | waiting | validating | success | timeout | declined
  const [statusMessage, setStatusMessage] = useState('');
  const [statusError, setStatusError] = useState('');
  const [checkoutRequestId, setCheckoutRequestId] = useState('');
  const [transactionToken, setTransactionToken] = useState('');

  const totalAmount =
    allBookingInformation?.fareSummry?.grandTotal ||
    fareSummery?.grandTotal ||
    allBookingInformation?.totalprice ||
    0;

  const cleanupTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => cleanupTimer(), []);

  const validatePhone = (value) => {
    // Only allow numeric values and exactly 10 digits
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length > 10) {
      setPhoneError('Phone number cannot exceed 10 digits');
      return false;
    } else if (numericValue.length > 0 && numericValue.length < 10) {
      setPhoneError('Phone number must be exactly 10 digits');
      return false;
    } else {
      setPhoneError('');
      return true;
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Remove any non-numeric characters and limit to 10 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    validatePhone(numericValue);
    setPhone(numericValue);
  };

  // Check if all required fields are filled
  const areAllFieldsFilled = () => {
    const email = passengerInformation?.login_email || '';
    const mobile = passengerInformation?.login_mobile || '';
    const firstName = passengerInformation?.first_name || '';
    const lastName = passengerInformation?.last_name || '';
    const idNumber = passengerInformation?.id_number || '';

    return !!(email && mobile && firstName && lastName && idNumber);
  };

  const handleTicketTracking = async (id) => {
    setLoading(false);
    const response = await fetch(
      `${process.env.REACT_APP_API_MODULE_DOMAIN}/tickets/bookingid/${id}`
    );
    const result = await response.json();

    if (result?.status === 'success') {
      dispatch(ticketTracking(result?.data));
      history.push('/ticket-traking');
      toast.success('Payment completed');
    } else {
      toast.error(result?.message || 'Unable to load booking');
    }
  };

  const finalizeBooking = () => {
    setPhase('success');
    setStatusMessage('Payment confirmed. Finishing booking…');
    setStatusError('');
    setLoading(true);
    PaymentHelper(allBookingInformation, dispatch)
      .then((res) => {
        handleTicketTracking(res?.data?.booking_id);
      })
      .catch((err) => {
        setLoading(false);
        setPhase('waiting');
        const message =
          err?.message ||
          err?.errors ||
          'Unable to complete booking. Please try again.';
        toast.error(message);
        setStatusError(message);
      });
  };

  const validateTransaction = async (
    checkoutId = checkoutRequestId,
    token = transactionToken
  ) => {
    if (!checkoutId || !token) {
      toast.error('Missing Mpesa confirmation details.');
      return;
    }
    setPhase('validating');
    setStatusError('');
    try {
      const formData = new FormData();
      formData.append('checkout_request_id', checkoutId);
      formData.append('transection_id', token);

      const response = await fetch(
        `${process.env.REACT_APP_API_MODULE_DOMAIN}/paymethods/mpesa/mpesa-validate`,
        {
          method: 'POST',
          body: formData,
        }
      );
      const result = await response.json();

      if (result?.status === 'Success') {
        finalizeBooking();
      } else {
        setPhase('idle');
        const message =
          result?.message?.errorMessage ||
          result?.message ||
          'Mpesa validation failed. Please approve the payment on your phone.';
        setStatusError(message);
        toast.error(message);
      }
    } catch (error) {
      setPhase('idle');
      setStatusError('Unable to validate payment. Please try again.');
      toast.error('Unable to validate payment. Please try again.');
    }
  };

  const scheduleValidation = (checkoutId, token) => {
    cleanupTimer();
    timerRef.current = setTimeout(() => {
      validateTransaction(checkoutId, token);
    }, MPESA_WAIT_TIME);
  };

  const requestStkPush = async () => {
    // First validate the main form - this will focus on empty fields
    if (validateForm && !validateForm()) {
      // Don't show any error - let the field validation handle it
      return;
    }

    if (!totalAmount) {
      toast.error('Total amount is missing.');
      return;
    }

    if (!phone || phone.length !== 10 || !phone.startsWith('0')) {
      // Focus on Mpesa phone field and show red border
      const mpesaField = document.getElementById('mpesa-phone');
      if (mpesaField) {
        mpesaField.focus();
        mpesaField.style.borderColor = 'red';
        mpesaField.style.boxShadow = '0 0 0 2px rgba(255, 0, 0, 0.3)';
        setTimeout(() => {
          mpesaField.style.borderColor = '';
          mpesaField.style.boxShadow = '';
        }, 3000);
      }
      setPhoneError('Enter a valid Mpesa number e.g. 07XXXXXXXX');
      return;
    }
    setPhase('requesting');
    setStatusMessage('');
    setStatusError('');

    try {
      const formData = new FormData();
      formData.append('phone', phone);
      formData.append('amount', totalAmount);

      const response = await fetch(
        `${process.env.REACT_APP_API_MODULE_DOMAIN}/paymethods/mpesa/mpesa_pay`,
        {
          method: 'POST',
          body: formData,
        }
      );
      const result = await response.json();

      if (result?.status === 'Success') {
        setCheckoutRequestId(result?.checkout_request_id);
        setTransactionToken(result?.transection_id);
        setStatusMessage(
          `${result?.message || 'STK push sent.'
          } Waiting for payment validation (up to 30 seconds)…`
        );
        setPhase('waiting');
        scheduleValidation(result?.checkout_request_id, result?.transection_id);
        toast.success('Request sent. Waiting for payment validation…');
      } else {
        setPhase('idle');
        const message =
          result?.message ||
          'Could not start Mpesa payment. Please confirm your details.';
        setStatusError(message);
        toast.error(message);
      }
    } catch (error) {
      setPhase('idle');
      setStatusError('Unable to reach Mpesa. Please try again.');
      toast.error('Unable to reach Mpesa. Please try again.');
    }
  };

  return (
    <MpesaWrapper>
      <MpesaBrand>
        M<span>-</span>Pesa
      </MpesaBrand>

      <HelperText>
        Enter your Mpesa-registered phone number. We will send an STK push for{' '}
        {webSettingData?.currency_symbol}
        {Number(totalAmount || 0).toFixed(2)}.
      </HelperText>

      <MpesaField>
        <MpesaLabel htmlFor="mpesa-phone">Mpesa phone number</MpesaLabel>
        <MpesaInput
          id="mpesa-phone"
          type="tel"
          maxLength="10"
          placeholder="07XXXXXXXX"
          value={phone}
          onChange={handlePhoneChange}
          onKeyPress={(e) => {
            // Only allow numeric keys
            const char = String.fromCharCode(e.which);
            if (!/[\d]/.test(char)) {
              e.preventDefault();
            }
          }}
          disabled={phase === 'waiting' || phase === 'validating'}
        />
      </MpesaField>

      {phoneError && (
        <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
          {phoneError}
        </div>
      )}

      <MpesaButtonGroup>
        <MpesaButton
          type="button"
          onClick={requestStkPush}
          disabled={phase === 'requesting' || phase === 'waiting' || phase === 'validating' || (validateForm && !validateForm()) || phoneError}
          btnbgcolor={webSettingData?.buttoncolor}
          btnbghvcolor={webSettingData?.buttoncolorhover}
          btntextcolor={webSettingData?.buttontextcolor}
        >
          {phase === 'requesting' ? 'Processing…' : 
           phase === 'waiting' ? 'Validating…' : 
           phase === 'validating' ? 'Processing…' : 
           'Pay Now'}
        </MpesaButton>
      </MpesaButtonGroup>

      {phase === 'requesting' && (
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px', color: '#666' }}>
          Initiating payment request...
        </div>
      )}
      
      {phase === 'waiting' && (
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px', color: '#666' }}>
          Waiting for Mpesa confirmation...
        </div>
      )}
      
      {phase === 'validating' && (
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px', color: '#666' }}>
          Validating payment status...
        </div>
      )}
      
      {phase === 'success' && (
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px', color: '#28a745' }}>
          Payment successful! Redirecting...
        </div>
      )}
      
      {phase === 'timeout' && (
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px', color: '#dc3545' }}>
          Payment timed out. Please try again.
        </div>
      )}
      
      {phase === 'declined' && (
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px', color: '#dc3545' }}>
          Payment declined. Please check your account and try again.
        </div>
      )}

      {phase === 'validating' && (
        <div>
          <Spinner />
        </div>
      )}

      {(statusMessage || statusError) && (
        <StatusMessage error={Boolean(statusError)}>
          {statusError || statusMessage}
        </StatusMessage>
      )}
    </MpesaWrapper>
  );
};

export default Mpesa;


